/**
 * Shared CreateSessionModal - extracted from TaigaSheetDetailPage.tsx.
 * Used by both TaigaSheetDetailPage and TaigaMentionsSection.
 *
 * 2 つのモードを持つ:
 * - session モード (nodeId あり): 既存ノードに対して session 作成 / Slack thread 紐付けを行う
 * - node 新規作成モード (isCreateNodeMode): prompt を入力してノードを新規作成する。
 *   prompt の先頭 100 文字が node 名としてバックエンドで保存される。
 *   「Create Session」で node + session を一度に作成、「ノードで作成」で session なしの
 *   ただのノードとして作成する。
 */
import type { FC } from "react"
import { useEffect, useState } from "react"

import styles from "../TaigaSheetDetailPage.module.scss"

// ─── Exported utilities ──────────────────────────────────────────

/**
 * Slack URL から thread_ts を抽出する。
 * 形式1: https://souhl-inc.slack.com/archives/CHANNEL/p1771786120321049
 *   → path 末尾の p<ts> を "1771786120.321049" に変換
 * 形式2: https://souhl-inc.slack.com/archives/CHANNEL/p...?thread_ts=1771786120.321049&cid=...
 *   → query param の thread_ts をそのまま使用
 * 不正な URL の場合は null を返す。
 */
export const extractSlackInfo = (
	url: string
): { threadTs: string; channelId: string } | null => {
	try {
		const parsed = new URL(url)
		if (!parsed.hostname.endsWith(".slack.com")) return null

		// channelId を path から抽出 (/archives/CHANNEL_ID/p...)
		const channelMatch = parsed.pathname.match(/\/archives\/([A-Z0-9]+)\//)
		if (!channelMatch) return null
		const channelId = channelMatch[1]

		// 形式2: thread_ts パラメータがある場合はそちらを優先
		const threadTsParam = parsed.searchParams.get("thread_ts")
		if (threadTsParam && /^\d+\.\d+$/.test(threadTsParam)) {
			return { threadTs: threadTsParam, channelId }
		}

		// 形式1: path から抽出 (/archives/CHANNEL/p1771786120321049)
		const pathMatch = parsed.pathname.match(/\/p(\d{10})(\d{6})$/)
		if (pathMatch) {
			return { threadTs: `${pathMatch[1]}.${pathMatch[2]}`, channelId }
		}

		return null
	} catch {
		return null
	}
}

// モバイル判定 hook
// taiga-browser 拡張 (Cursor webview) の iframe 内はパネル幅が狭くても PC なので、
// 幅判定だけだとスマホ扱いになり session 選択 checkbox 非表示・Slack がスマホ用経路
// (画面ごと遷移) になってしまう。iframe 埋め込み時は常に PC 扱いにする。
export const useIsMobile = () => {
	const [isMobile, setIsMobile] = useState(false)
	useEffect(() => {
		if (window.parent !== window) return
		const check = () => setIsMobile(window.innerWidth < 768)
		check()
		window.addEventListener("resize", check)
		return () => window.removeEventListener("resize", check)
	}, [])
	return isMobile
}

// ─── Component ───────────────────────────────────────────────────

const CreateSessionModal: FC<{
	// --- session モード (既存ノードに session を作成する) ---
	nodeId?: string
	nodeName?: string
	onSubmit?: (nodeId: string, prompt: string) => void
	onAttach?: (
		nodeId: string,
		threadTs: string,
		channelId: string,
		memo: string | null
	) => void
	isAttachLoading?: boolean
	// --- node 新規作成モード (prompt の先頭 100 文字が node 名になる) ---
	isCreateNodeMode?: boolean
	onCreateNodeOnly?: (prompt: string) => void
	onCreateNodeWithSession?: (prompt: string, sessionPrompt: string) => void
	onClose: () => void
	isLoading: boolean
}> = ({
	nodeId,
	nodeName,
	onSubmit,
	onAttach,
	isAttachLoading = false,
	isCreateNodeMode = false,
	onCreateNodeOnly,
	onCreateNodeWithSession,
	onClose,
	isLoading,
}) => {
	const [prompt, setPrompt] = useState("")
	const [attachMemo, setAttachMemo] = useState("")
	const [slackUrl, setSlackUrl] = useState("")
	const [slackUrlError, setSlackUrlError] = useState("")
	// fable あり選択時は prompt 末尾に「 fable」を追記して投稿する
	const [withFable, setWithFable] = useState(false)
	// remote control あり選択時は prompt 末尾に「 /rc」を追記して投稿する
	// (cc-server が /rc トークンを検出して Remote Control を有効化する)
	const [withRemoteControl, setWithRemoteControl] = useState(false)
	const isMobile = useIsMobile()

	const anyLoading = isLoading || isAttachLoading

	// session に投稿する prompt (fable あり時は末尾に「 fable」、RC あり時は「 /rc」を追記)
	const sessionPrompt = `${prompt}${withFable ? " fable" : ""}${withRemoteControl ? " /rc" : ""}`

	const handleSubmit = () => {
		if (isCreateNodeMode) {
			onCreateNodeWithSession?.(prompt, sessionPrompt)
			return
		}
		if (nodeId) {
			onSubmit?.(nodeId, sessionPrompt)
		}
	}

	const handleCreateNodeOnly = () => {
		onCreateNodeOnly?.(prompt)
	}

	const handleAttach = () => {
		if (!nodeId) return
		const info = extractSlackInfo(slackUrl.trim())
		if (!info) {
			setSlackUrlError("Invalid Slack URL")
			return
		}
		setSlackUrlError("")
		onAttach?.(nodeId, info.threadTs, info.channelId, attachMemo.trim() || null)
	}

	return (
		<div className={styles.modalOverlay} onClick={onClose}>
			<div className={styles.modal} onClick={(e) => e.stopPropagation()}>
				<h3 className={styles.modalTitle}>
					{isCreateNodeMode ? "Create Node" : "Create Session"}
				</h3>
				<p className={styles.modalHint}>
					{isCreateNodeMode
						? "Prompt の先頭 100 文字がノード名になります。「ノードで作成」は session を作らずノードのみ作成します"
						: "This will create a new Slack thread and start an AI session for this leaf"}
				</p>
				{!isCreateNodeMode && (
					<div className={styles.modalField}>
						<label>Node</label>
						<div className={styles.readOnlyNodeName}>{nodeName}</div>
					</div>
				)}

				{/* Attach Slack Thread section (既存ノードへの紐付けなので session モードのみ) */}
				{!isCreateNodeMode && (
					<>
						<div className={styles.attachSection}>
							<label className={styles.attachLabel}>Attach Slack Thread</label>
							<p className={styles.attachHint}>
								Attach an existing Slack thread instead of creating a new one
							</p>
							<div className={styles.attachRow}>
								<input
									className={`${styles.attachInput}${slackUrlError ? ` ${styles.attachInputError}` : ""}`}
									type="text"
									placeholder="https://souhl-inc.slack.com/archives/..."
									value={slackUrl}
									onChange={(e) => {
										setSlackUrl(e.target.value)
										if (slackUrlError) setSlackUrlError("")
									}}
									onKeyDown={(e) => {
										if (
											e.key === "Enter" &&
											!e.nativeEvent.isComposing &&
											slackUrl.trim() &&
											!anyLoading
										) {
											e.preventDefault()
											handleAttach()
										}
									}}
								/>
								<button
									className={styles.attachBtn}
									onClick={handleAttach}
									disabled={!slackUrl.trim() || anyLoading}
								>
									{isAttachLoading ? "Attaching..." : "Attach"}
								</button>
							</div>
							{slackUrlError && (
								<span className={styles.attachError}>{slackUrlError}</span>
							)}
							<div
								className={styles.modalField}
								style={{ marginTop: 8, marginBottom: 0 }}
							>
								<label>Memo (optional)</label>
								<textarea
									className={styles.promptInput}
									placeholder="メモ（省略可）"
									value={attachMemo}
									onChange={(e) => setAttachMemo(e.target.value)}
									maxLength={2000}
									rows={2}
								/>
								<span
									className={`${styles.memoCharCount} ${attachMemo.length >= 1800 ? styles.memoCharCountWarn : ""}`}
								>
									{attachMemo.length}/2000
								</span>
							</div>
						</div>

						<div className={styles.modalDivider} />
					</>
				)}

				<div className={styles.modalField}>
					<label>Prompt</label>
					<textarea
						className={styles.promptInput}
						placeholder="Enter your prompt..."
						value={prompt}
						onChange={(e) => setPrompt(e.target.value)}
						onKeyDown={(e) => {
							if (
								e.key === "Enter" &&
								(isMobile || e.metaKey || e.ctrlKey) &&
								!e.nativeEvent.isComposing &&
								prompt.trim() &&
								!anyLoading
							) {
								e.preventDefault()
								if (isCreateNodeMode && e.shiftKey) {
									handleCreateNodeOnly()
								} else {
									handleSubmit()
								}
							}
						}}
						rows={5}
						autoFocus={isCreateNodeMode}
					/>
					<span className={styles.hint}>
						{isCreateNodeMode
							? "Cmd+Enter で Session 作成 / Shift+Cmd+Enter でノードのみ作成"
							: "Cmd+Enter で作成"}
					</span>
				</div>
				<div className={styles.modalField}>
					<label>fable</label>
					<div className={styles.fableToggleRow}>
						<button
							type="button"
							className={`${styles.fableToggleBtn} ${
								!withFable ? styles.fableToggleBtnActive : ""
							}`}
							onClick={() => setWithFable(false)}
							disabled={anyLoading}
						>
							fable なし
						</button>
						<button
							type="button"
							className={`${styles.fableToggleBtn} ${
								withFable ? styles.fableToggleBtnActive : ""
							}`}
							onClick={() => setWithFable(true)}
							disabled={anyLoading}
						>
							fable あり
						</button>
					</div>
					<span className={styles.hint}>
						あり: prompt 末尾に「 fable」を追記して投稿
					</span>
				</div>
				<div className={styles.modalField}>
					<label>remote control</label>
					<div className={styles.fableToggleRow}>
						<button
							type="button"
							className={`${styles.fableToggleBtn} ${
								!withRemoteControl ? styles.fableToggleBtnActive : ""
							}`}
							onClick={() => setWithRemoteControl(false)}
							disabled={anyLoading}
						>
							RC なし
						</button>
						<button
							type="button"
							className={`${styles.fableToggleBtn} ${
								withRemoteControl ? styles.fableToggleBtnActive : ""
							}`}
							onClick={() => setWithRemoteControl(true)}
							disabled={anyLoading}
						>
							RC あり
						</button>
					</div>
					<span className={styles.hint}>
						あり: prompt 末尾に「 /rc」を追記して投稿（Claude
						アプリからこのセッションに話しかけられるようになる）
					</span>
				</div>
				<div className={styles.modalActions}>
					<button
						className={styles.cancelBtn}
						onClick={onClose}
						disabled={anyLoading}
					>
						Cancel
					</button>
					{isCreateNodeMode && (
						<button
							className={styles.createBtn}
							onClick={handleCreateNodeOnly}
							disabled={!prompt.trim() || anyLoading}
						>
							{isLoading ? "Creating..." : "ノードで作成"}
						</button>
					)}
					<button
						className={styles.createBtn}
						onClick={handleSubmit}
						disabled={!prompt.trim() || anyLoading}
					>
						{isLoading ? "Creating..." : "Create Session"}
					</button>
				</div>
			</div>
		</div>
	)
}

export default CreateSessionModal
